import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue';
import {
  formatToDate,
  fromISO,
  formatDatePickerDay,
  isWithinHours,
} from '@/utils/datetime';
import { PropType } from 'vue';
import { DynamicList } from '@/interfaces/model/general/dynamicList';
import SelectStatus from '@/components/SelectStatus.vue';
import { getFullName } from '@/utils/string';
import sharedMessages from '@/lib/shared-messages';
import SelectInTextField from '@/components/SelectInTextField.vue';
import { readUserLocale, readIsFeatureEnabled } from '@/store/main/getters';
import { store } from '@/store/index';
import { i18n } from '@/plugins/vue-i18n';

interface FilterCriteria {
  listName: string;
  summary: string;
  updatedStartDate: string | null;
  updatedEndDate: string | null;
  createdStartDate: string | null;
  createdEndDate: string | null;
  status: string[];
  creator: string[];
  targetLaws: string[];
  targetRegulations: string[];
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CheckListFilterPanel',
  props: {
  dynamicLists: {
    type: Array as PropType<DynamicList[]>,
    required: true,
  },
  isFilterPanelVisible: {
    type: Boolean,
    required: true,
  },
},
  emits: ['cancelFilters', 'update:filteredDynamicLists'],
  setup(__props, { emit: emits }) {

const props = __props;

const locale = computed(() => readUserLocale(store));
const $t = i18n.t.bind(i18n);

const isChecklistLawRegulationEnabled = computed(() => {
  return (
    readIsFeatureEnabled(store)('use_new_regulatory_screening_ui') ?? false
  );
});

const isBeforeStart = (item: {
  is_query_ready: boolean;
  total: number;
  created_at: string;
}): boolean => {
  if (!item.is_query_ready) return true;
  if (
    isChecklistLawRegulationEnabled.value &&
    item.total === 0 &&
    isWithinHours(item.created_at, 24)
  ) {
    return true;
  }
  return false;
};

//Define proper type for filter criteria




const menuUpdatedStartDate = ref(false);
const menuUpdatedEndDate = ref(false);
const menuCreatedStartDate = ref(false);
const menuCreatedEndDate = ref(false);

const defaultFilterCriteria = (): FilterCriteria => ({
  listName: '',
  summary: '',
  updatedStartDate: null,
  updatedEndDate: null,
  createdStartDate: null,
  createdEndDate: null,
  status: [],
  creator: [],
  targetLaws: [],
  targetRegulations: [],
});

// Use proper type for filter criteria
const filterCriteria = ref<FilterCriteria>(defaultFilterCriteria());
const filterCriteriaTemp = ref<FilterCriteria>(defaultFilterCriteria());

const statusOptions = computed(() => {
  if (isChecklistLawRegulationEnabled.value) {
    return [
      { value: sharedMessages.statusBeforeStart, state: 'outlined-status' },
      { value: sharedMessages.statusStopped, state: 'impossible' },
      { value: sharedMessages.statusRunning, state: 'running' },
    ];
  }
  return [
    { value: sharedMessages.statusInPreparation, state: 'analyzing' },
    { value: sharedMessages.statusUpdateSuspended, state: 'impossible' },
    { value: sharedMessages.statusCreated, state: 'complete' },
  ];
});

// Divide status chip per design
const StatusChipDesign = (status: string): string => {
  switch (status) {
    case sharedMessages.statusInPreparation:
      return 'analyzing';
    case sharedMessages.statusBeforeStart:
      return 'outlined-status';
    case sharedMessages.statusUpdateSuspended:
    case sharedMessages.statusStopped:
      return 'impossible';
    case sharedMessages.statusCreated:
      return 'complete';
    case sharedMessages.statusRunning:
      return 'running';
    default:
      return '';
  }
};

const filteredCreatorOptions = computed(() => {
  // Create a Map to deduplicate creators by UUID
  const creatorMap = new Map();

  props.dynamicLists.forEach((list) => {
    const uuid = list.created_by; // Use UUID as unique identifier
    const displayName = getFullName(
      list.created_user,
      String($t(sharedMessages.defaultUser))
    );

    if (uuid && !creatorMap.has(uuid)) {
      creatorMap.set(uuid, {
        title: displayName,
        value: uuid, // Store UUID as value for filtering
        displayName: displayName, // Keep display name for reference
      });
    }
  });

  const options = Array.from(creatorMap.values());

  return options;
});

// Helper function to extract unique options from dynamic lists
const extractUniqueOptions = (
  lists: DynamicList[],
  fieldName: 'law_names' | 'regulation_names'
) => {
  const uniqueSet = new Set<string>();

  lists.forEach((list) => {
    const values = list[fieldName];
    if (values && values.length > 0) {
      values.forEach((value) => {
        if (value) {
          uniqueSet.add(value);
        }
      });
    }
  });

  return Array.from(uniqueSet)
    .sort()
    .map((item) => ({
      title: item,
      value: item,
    }));
};

const targetLawOptions = computed(() =>
  extractUniqueOptions(props.dynamicLists, 'law_names')
);

const targetRegulationOptions = computed(() =>
  extractUniqueOptions(props.dynamicLists, 'regulation_names')
);

// Helper function to get creator display name by UUID
const getCreatorDisplayName = (uuid: string): string => {
  const creator = props.dynamicLists.find((list) => list.created_by === uuid);
  if (creator && creator.created_user) {
    return getFullName(
      creator.created_user,
      String($t(sharedMessages.defaultUser))
    );
  }
  return String($t(sharedMessages.defaultUser));
};

// Properly typed removeFilter function
const removeFilter = (key: keyof FilterCriteria) => {
  if (key.includes('Date')) {
    (filterCriteria.value as Record<string, unknown>)[key] = null;
    (filterCriteriaTemp.value as Record<string, unknown>)[key] = null;
  } else if (Array.isArray(filterCriteria.value[key])) {
    (filterCriteria.value as Record<string, unknown>)[key] = [];
    (filterCriteriaTemp.value as Record<string, unknown>)[key] = [];
  } else {
    (filterCriteria.value as Record<string, unknown>)[key] = '';
    (filterCriteriaTemp.value as Record<string, unknown>)[key] = '';
  }
  applyFilters();
};

// Properly typed removeArrayFilterItem function
const removeArrayFilterItem = (
  key: keyof FilterCriteria,
  value: string | number
) => {
  const currentValue = filterCriteria.value[key];
  const currentTempValue = filterCriteriaTemp.value[key];

  if (Array.isArray(currentValue) && Array.isArray(currentTempValue)) {
    (filterCriteria.value as Record<string, unknown>)[key] =
      currentValue.filter((item: string) => item !== value);
    (filterCriteriaTemp.value as Record<string, unknown>)[key] =
      currentTempValue.filter((item: string) => item !== value);
  } else {
    (filterCriteria.value as Record<string, unknown>)[key] = '';
    (filterCriteriaTemp.value as Record<string, unknown>)[key] = '';
  }
  applyFilters();
};

const cancelFilters = () => {
  emits('cancelFilters', false);
};

const applyFilters = () => {
  filterCriteria.value = { ...filterCriteriaTemp.value };
  const value = filteredDynamicLists;
  emits('update:filteredDynamicLists', value);
  cancelFilters();
};

const resetFilters = () => {
  filterCriteria.value = defaultFilterCriteria();
  filterCriteriaTemp.value = { ...filterCriteria.value };
  applyFilters();
};

const filteredDynamicLists = computed(() => {
  return props.dynamicLists.filter((item) => {
    const matchesListName = filterCriteria.value.listName
      ? item.name
          .toLowerCase()
          .includes(filterCriteria.value.listName.toLowerCase())
      : true;

    // Old UI filters (flag OFF)
    const matchesSummary =
      isChecklistLawRegulationEnabled.value ||
      !filterCriteria.value.summary ||
      item.description
        .toLowerCase()
        .includes(filterCriteria.value.summary.toLowerCase());

    const matchesUpdatedDate =
      isChecklistLawRegulationEnabled.value ||
      ((!filterCriteria.value.updatedStartDate ||
        fromISO(item.updated_at).startOf('day') >=
          fromISO(filterCriteria.value.updatedStartDate).startOf('day')) &&
        (!filterCriteria.value.updatedEndDate ||
          fromISO(item.updated_at).startOf('day') <=
            fromISO(filterCriteria.value.updatedEndDate).startOf('day')));

    const matchesCreatedDate =
      (!filterCriteria.value.createdStartDate ||
        fromISO(item.created_at).startOf('day') >=
          fromISO(filterCriteria.value.createdStartDate).startOf('day')) &&
      (!filterCriteria.value.createdEndDate ||
        fromISO(item.created_at).startOf('day') <=
          fromISO(filterCriteria.value.createdEndDate).startOf('day'));

    // UUID-based creator filtering
    const matchesCreator = filterCriteria.value.creator.length
      ? filterCriteria.value.creator.includes(item.created_by || '')
      : true;

    const matchesStatus = filterCriteria.value.status.length
      ? filterCriteria.value.status.includes(
          isBeforeStart(item)
            ? isChecklistLawRegulationEnabled.value
              ? sharedMessages.statusBeforeStart
              : sharedMessages.statusInPreparation
            : item.suspended_at !== null
            ? isChecklistLawRegulationEnabled.value
              ? sharedMessages.statusStopped
              : sharedMessages.statusUpdateSuspended
            : isChecklistLawRegulationEnabled.value
            ? sharedMessages.statusRunning
            : sharedMessages.statusCreated
        )
      : true;

    // New UI filters (flag ON)
    const matchesTargetLaws =
      !isChecklistLawRegulationEnabled.value ||
      !filterCriteria.value.targetLaws.length ||
      filterCriteria.value.targetLaws.some((law) =>
        item.law_names?.some((lawName) => lawName === law)
      );

    const matchesTargetRegulations =
      !isChecklistLawRegulationEnabled.value ||
      !filterCriteria.value.targetRegulations.length ||
      filterCriteria.value.targetRegulations.some((regulation) =>
        item.regulation_names?.some((regName) => regName === regulation)
      );

    return (
      matchesListName &&
      matchesSummary &&
      matchesUpdatedDate &&
      matchesCreatedDate &&
      matchesCreator &&
      matchesStatus &&
      matchesTargetLaws &&
      matchesTargetRegulations
    );
  });
});

const hasFilterCriteria = computed(() => {
  const hasBasicFilters = !!(
    filterCriteria.value.listName ||
    filterCriteria.value.createdStartDate ||
    filterCriteria.value.createdEndDate ||
    filterCriteria.value.creator.length > 0 ||
    filterCriteria.value.status.length > 0
  );
  const hasOldFilters =
    !isChecklistLawRegulationEnabled.value &&
    !!(
      filterCriteria.value.summary ||
      filterCriteria.value.updatedStartDate ||
      filterCriteria.value.updatedEndDate
    );
  const hasLawRegulationFilters =
    isChecklistLawRegulationEnabled.value &&
    (filterCriteria.value.targetLaws.length > 0 ||
      filterCriteria.value.targetRegulations.length > 0);
  return hasBasicFilters || hasOldFilters || hasLawRegulationFilters;
});

return { __sfc: true,locale, $t, isChecklistLawRegulationEnabled, isBeforeStart, props, emits, menuUpdatedStartDate, menuUpdatedEndDate, menuCreatedStartDate, menuCreatedEndDate, defaultFilterCriteria, filterCriteria, filterCriteriaTemp, statusOptions, StatusChipDesign, filteredCreatorOptions, extractUniqueOptions, targetLawOptions, targetRegulationOptions, getCreatorDisplayName, removeFilter, removeArrayFilterItem, cancelFilters, applyFilters, resetFilters, filteredDynamicLists, hasFilterCriteria, formatToDate, formatDatePickerDay, SelectStatus, sharedMessages, SelectInTextField }
}

})